// PODIUM overlay (§9.5, §14.2): "PODIUM BRAWL — knock the champ off!" with the timer, the final
// top 3 on gold / silver / bronze medals, your place, and this match's rewards from the profile
// service (§7.4): XP lines ticking up, the level bar filling (LEVEL UP! with confetti), Bonk Bucks,
// new unlocks and finished challenges. Then the Tapestry POSTCARD, REMATCH (ready, with how many
// pressed it) and LEAVE. Not modal: the Podium Brawl stays playable behind it; Enter / (A) with
// nothing focused presses REMATCH, View / Tab brings focus into the panel.
import { RoomPhase } from '@shared/ids';
import type { PlayerDTO } from '@shared/protocol/messages';
import type { MatchRewards } from '../../profile/ProfileService';
import { xpForLevel } from '../../profile/progression';
import { confetti, countUp } from '../anim';
import type { LiveScreen, NavAction } from '../contracts';
import type { UiDeps } from '../deps';
import { actionCap } from '../glyphView';
import { CATEGORY_ICONS, icon, identityBadge } from '../icons';
import { clockText, ordinal } from '../labels';
import { el, replayClass, setHidden, setStyle, setText, toggleClass } from '../ui';
import { button, setLabel } from '../widgets';

const MEDALS = ['gold', 'silver', 'bronze'] as const;
const LINE_STAGGER_MS = 180;

export function create(deps: UiDeps): LiveScreen {
  const room = deps.room;
  const profiles = deps.services.profiles;
  const timer = el('span', { class: 'chip ink podium-timer' });
  const medals = el('div', { class: 'podium-medals' });
  const mine = el('div', { class: 'podium-mine' });
  const rwLines = el('div', { class: 'rw-lines' });
  const rwLevel = el('span', { class: 'lvl-badge' }, el('small', { text: 'LV' }), el('span', { class: 'lvl-num' }));
  const rwFill = el('i');
  const rwXp = el('span', { class: 'rw-xp' });
  const rwBucks = el('span', { class: 'rw-bucks' });
  const rwExtra = el('div', { class: 'rw-extra' });
  const rewards = el('div', { class: 'podium-rewards' },
    el('div', { class: 'rw-head' }, el('span', { class: 'rw-title', text: 'YOUR REWARDS' }), el('span', { class: 'chip yellow' }, icon('coin'), rwBucks)),
    rwLines,
    el('div', { class: 'rw-level' }, rwLevel, el('div', { class: 'bar xp rw-bar' }, rwFill), rwXp),
    rwExtra);
  const rematchCount = el('span', { class: 'rm-count' });
  const rematch = button({ label: 'REMATCH', icon: 'rematch', variant: 'pink', cls: 'rematch-btn', onClick: () => pressRematch() });
  const rematchHint = el('span', { class: 'ready-hint' });
  const tapestry = button({ label: 'POSTCARD', title: 'Postcard', icon: 'postcard', variant: 'sky', onClick: () => deps.nav.show('tapestry') });
  const leave = button({ label: 'LEAVE', title: 'Leave', icon: 'exit', variant: 'cream', onClick: () => deps.actions.leave() });
  // Touch: the panel shows only your place and the actions until STANDINGS unfolds it.
  const more = button({ label: 'STANDINGS', title: 'Standings', icon: 'trophy', variant: 'yellow', size: 'small', cls: 'podium-more', onClick: () => {
    panel.classList.toggle('expanded');
  } });
  const panel = el('div', { class: 'panel podium-panel with-ribbon' },
    el('div', { class: 'ribbon gold', text: 'FINAL STANDINGS' }),
    medals, mine, rewards,
    el('div', { class: 'podium-actions' },
      el('div', { class: 'ready-wrap' }, rematch, rematchHint),
      rematchCount,
      el('div', { class: 'row center wrap' }, more, tapestry, leave)));
  const root = el('div', { class: 'screen podium', attrs: { 'data-nofocus': true } },
    el('div', { class: 'podium-banner' },
      el('div', { class: 'pb-title inked', text: 'PODIUM BRAWL' }),
      el('div', { class: 'pb-sub' }, el('span', { text: 'Knock the champ off!' }), timer)),
    panel);

  let shownRewards: MatchRewards | null = null;
  let celebrated = false;
  const timers: number[] = [];

  function pressRematch(): void {
    if (room.spectator) return;
    deps.services.playUi('coin');
    deps.actions.send({ t: 'rematch' });
    replayClass(rematch, 'pressed');
  }

  function renderStandings(): void {
    const dto = room.dto;
    if (!dto) return;
    const colorblind = deps.settings().accessibility.colorblind;
    const end = room.matchEnd;
    const rows = end
      ? [...end.standings].sort((a, b) => a.place - b.place || b.score - a.score)
      : dto.players.flatMap((p) => (p ? [{ slot: p.slot, score: p.score, place: 0 }] : [])).sort((a, b) => b.score - a.score).map((r, i) => ({ ...r, place: i + 1 }));
    const key = rows.map((r) => `${r.slot}:${r.score}:${r.place}`).join('|') + colorblind;
    if (medals.dataset.k === key) return;
    medals.dataset.k = key;
    medals.replaceChildren(...rows.slice(0, 3).map((r, i) => {
      const p: PlayerDTO | null = dto.players[r.slot] ?? null;
      return el('div', { class: `medal-row ${MEDALS[Math.min(2, Math.max(0, r.place - 1))]}${r.slot === room.slot ? ' me' : ''}`, style: { 'animation-delay': `${120 + i * 140}ms` } },
        el('span', { class: 'medal' }, el('span', { class: 'medal-num', text: String(r.place) })),
        identityBadge(p?.identity ?? r.slot, colorblind),
        el('span', { class: 'medal-name ellipsis', text: p?.name ?? `Bean ${r.slot + 1}` }),
        el('span', { class: 'medal-pts' }, el('b', { text: String(r.score) }), el('small', { text: 'pts' })));
    }));
    const meRow = rows.find((r) => r.slot === room.slot);
    setText(mine, meRow ? (meRow.place === 1 ? 'YOU WON THE MATCH!' : `You placed ${ordinal(meRow.place)} with ${meRow.score} pts`) : 'Thanks for watching!');
    toggleClass(mine, 'won', meRow?.place === 1);
    if (meRow?.place === 1 && !celebrated) {
      celebrated = true;
      timers.push(window.setTimeout(() => {
        const r = mine.getBoundingClientRect();
        const host = root.getBoundingClientRect();
        confetti(root, r.left - host.left + r.width / 2, r.top - host.top, ['#FFD24A', '#FF4F9A', '#4CB8FF', '#3DDC97', '#FFFFFF'], 70, 1.2);
      }, 500));
    }
  }

  function renderRematch(): void {
    const dto = room.dto;
    if (!dto) return;
    const pressed = room.slot >= 0 && dto.rematch.includes(room.slot);
    const humans = dto.players.filter((p) => p !== null && !p.isBot).length;
    toggleClass(rematch, 'on', pressed);
    setLabel(rematch, pressed ? 'REMATCH ✓' : 'REMATCH');
    setText(rematchCount, `${dto.rematch.length}/${Math.max(1, humans)} want a rematch`);
    setHidden(rematch, room.spectator);
  }

  /** Animates this match's XP lines, the level bar and Bucks (§7.4). */
  function renderRewards(r: MatchRewards | null): void {
    setHidden(rewards, r === null);
    if (!r || r === shownRewards) return;
    shownRewards = r;
    for (const t of timers.splice(0)) clearTimeout(t);
    const lines = r.lines.filter((l) => l.xp > 0);
    rwLines.replaceChildren(...lines.map((l, i) => el('div', { class: 'rw-line', style: { 'animation-delay': `${300 + i * LINE_STAGGER_MS}ms` } },
      el('span', { class: 'rw-l', text: l.count > 1 ? `${l.label} ×${l.count}` : l.label }), el('b', { class: 'rw-v', text: `+${l.xp} XP` }))));
    const lvlNum = rwLevel.querySelector('.lvl-num') as HTMLElement;
    setText(lvlNum, String(r.levelBefore));
    setStyle(rwFill, 'width', `${Math.round((100 * r.xpBefore) / xpForLevel(r.levelBefore))}%`);
    setText(rwXp, `+${r.xp} XP`);
    setText(rwBucks, `+0`);
    rwExtra.replaceChildren();
    const start = 300 + lines.length * LINE_STAGGER_MS + 200;
    timers.push(window.setTimeout(() => {
      countUp(rwBucks, 0, r.bucks, 900, (n) => `+${Math.round(n)}`);
      deps.services.playUi('coin');
      if (r.levelAfter > r.levelBefore) {
        // Fill to the top, pop LEVEL UP, then fill the new level's share.
        setStyle(rwFill, 'width', '100%');
        timers.push(window.setTimeout(() => {
          setText(lvlNum, String(r.levelAfter));
          replayClass(rwLevel, 'level-up');
          rewards.classList.add('leveled');
          setStyle(rwFill, 'transition', 'none');
          setStyle(rwFill, 'width', '0%');
          void rwFill.offsetWidth;
          setStyle(rwFill, 'transition', '');
          setStyle(rwFill, 'width', `${Math.round((100 * r.xpAfter) / xpForLevel(r.levelAfter))}%`);
          const b = rwLevel.getBoundingClientRect();
          const host = root.getBoundingClientRect();
          confetti(root, b.left - host.left + b.width / 2, b.top - host.top, ['#3DDC97', '#FFD24A', '#FFFFFF'], 40);
          deps.services.playUi('drumroll');
        }, 850));
      } else {
        setStyle(rwFill, 'width', `${Math.round((100 * r.xpAfter) / xpForLevel(r.levelAfter))}%`);
      }
      const extras: HTMLElement[] = [];
      if (r.levelAfter > r.levelBefore) extras.push(el('span', { class: 'chip gold rw-up' }, icon('star'), `LEVEL UP! LV ${r.levelAfter}`));
      for (const it of r.unlocks.slice(0, 3)) extras.push(el('span', { class: 'chip pink rw-unlock' }, icon(CATEGORY_ICONS[it.cat]), `NEW: ${it.name}`));
      if (r.challengesCompleted.length > 0) extras.push(el('span', { class: 'chip mint' }, icon('flag'), `${r.challengesCompleted.length} challenge${r.challengesCompleted.length === 1 ? '' : 's'} done`));
      timers.push(window.setTimeout(() => rwExtra.replaceChildren(...extras), r.levelAfter > r.levelBefore ? 1100 : 400));
    }, start));
  }

  room.onChange(() => {
    if (root.hidden) return;
    renderStandings();
    renderRematch();
  });
  profiles.onRewards((r) => {
    if (!root.hidden) renderRewards(r);
  });

  return {
    el: root,
    onShow() {
      celebrated = false;
      shownRewards = null;
      renderStandings();
      renderRematch();
      renderRewards(profiles.lastRewards());
      const dev = deps.services.input.device;
      rematchHint.replaceChildren(...(dev === 'touch' ? [] : [actionCap(deps, 'ready')]));
    },
    onHide() {
      for (const t of timers.splice(0)) clearTimeout(t);
    },
    onNav(action: NavAction) {
      const focused = document.activeElement instanceof HTMLElement && root.contains(document.activeElement);
      if (action === 'confirm' && !focused) {
        pressRematch();
        return true;
      }
      if (action === 'scoreboard') {
        (focused ? (document.activeElement as HTMLElement) : rematch).focus();
        return true;
      }
      return false;
    },
    update() {
      const dto = room.dto;
      if (!dto) return;
      setText(timer, dto.phase === RoomPhase.PODIUM ? clockText(room.secondsUntil(dto.phaseEndTick)) : '');
    },
  };
}
